import { useEffect, useState } from 'react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/hooks/useLanguage';

// Utiliser la clé publique Stripe depuis les variables d'environnement
const STRIPE_PUBLIC_KEY = import.meta.env.VITE_STRIPE_PUBLIC_KEY;

if (!STRIPE_PUBLIC_KEY) {
  console.error('❌ VITE_STRIPE_PUBLIC_KEY is not defined in environment variables');
}

const stripePromise = STRIPE_PUBLIC_KEY ? loadStripe(STRIPE_PUBLIC_KEY) : null;

interface StripeEmbeddedCheckoutProps {
  isOpen: boolean;
  onClose: () => void;
  totalAmount: number;
  amountForStripe: number;
  currencyForStripe: string;
  originalCurrency: string;
  onSuccess: () => void;
}

export const StripeEmbeddedCheckout = ({
  isOpen,
  onClose,
  totalAmount,
  amountForStripe,
  currencyForStripe,
  originalCurrency,
  onSuccess
}: StripeEmbeddedCheckoutProps) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    if (isOpen && !clientSecret) {
      createCheckoutSession();
    }
  }, [isOpen]);

  const createCheckoutSession = async () => {
    try {
      setLoading(true);
      console.log('[StripeEmbeddedCheckout] Creating checkout session...');
      console.log('[StripeEmbeddedCheckout] Stripe key present:', !!STRIPE_PUBLIC_KEY);
      
      // Get pending cart data from session storage
      const pendingCartData = sessionStorage.getItem('pendingCart');
      if (!pendingCartData) {
        throw new Error('Données du panier introuvables');
      }

      const { items, relayId, clientId } = JSON.parse(pendingCartData);
      
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        throw new Error('Vous devez être connecté pour payer');
      }

      console.log('[StripeEmbeddedCheckout] Invoking create-checkout function...');
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: {
          cartItems: items,
          relayId,
          clientId,
          totalAmount,
          amountForStripe,
          currencyForStripe,
          originalCurrency
        }
      });

      if (error) {
        console.error('[StripeEmbeddedCheckout] Function error:', error);
        throw error;
      }
      if (!data?.clientSecret) {
        console.error('[StripeEmbeddedCheckout] No clientSecret in response:', data);
        throw new Error('Erreur lors de la création de la session');
      }

      console.log('[StripeEmbeddedCheckout] Client secret received:', data.clientSecret.substring(0, 20) + '...');
      setClientSecret(data.clientSecret);
    } catch (error: any) {
      console.error('[StripeEmbeddedCheckout] Error creating session:', error);
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de créer la session de paiement',
        variant: 'destructive'
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    console.log('[StripeEmbeddedCheckout] Payment completed, verifying...');
    try {
      // Récupérer le sessionId depuis le clientSecret
      const sessionId = clientSecret?.split('_secret_')[0];
      if (!sessionId) {
        throw new Error('Session ID non trouvé');
      }

      // Vérifier le paiement côté serveur
      const { data, error } = await supabase.functions.invoke('verify-payment', {
        body: { sessionId }
      });

      if (error) throw error;

      if (data?.success) {
        console.log('[StripeEmbeddedCheckout] Payment verified successfully');
        onSuccess();
        onClose();
      } else {
        throw new Error('Le paiement n\'a pas pu être vérifié');
      }
    } catch (error: any) {
      console.error('[StripeEmbeddedCheckout] Error verifying payment:', error);
      toast({
        title: 'Erreur de vérification',
        description: error.message || 'Impossible de vérifier le paiement',
        variant: 'destructive'
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('payment.secureCheckout')}</DialogTitle>
        </DialogHeader>
        
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {!loading && !clientSecret && (
          <div className="flex justify-center items-center py-12">
            <p className="text-muted-foreground">{t('payment.loadingCheckout')}</p>
          </div>
        )}

        {clientSecret && stripePromise && (
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{ clientSecret, onComplete: handleComplete }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}

        {clientSecret && !stripePromise && (
          <div className="flex justify-center items-center py-12 text-center">
            <div>
              <p className="text-destructive font-semibold mb-2">Configuration Stripe manquante</p>
              <p className="text-sm text-muted-foreground">La clé publique Stripe n'est pas configurée.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
